import React, { useState, useEffect, useRef } from 'react';
import { searchSchools, resolveDisplayName } from '../utils/schoolData';

export default function Step1Schools({ stepIndicator, schools, setSchools, onNext, hpTheme = false }) {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const results = searchSchools(inputValue);
    setSuggestions(results);
    setHighlightedIndex(-1);
  }, [inputValue]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (
        inputRef.current && !inputRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addSchool = (rawName) => {
    const name = resolveDisplayName(rawName.trim());
    if (!name) return;
    if (schools.some(s => s.toLowerCase() === name.toLowerCase())) return;
    setSchools([...schools, name]);
    setInputValue('');
    setSuggestions([]);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const removeSchool = (name) => setSchools(schools.filter(s => s !== name));

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
        addSchool(suggestions[highlightedIndex].name);
      } else if (inputValue.trim()) {
        addSchool(inputValue.trim());
      }
    } else if (e.key === 'Escape') {
      setSuggestions([]);
      setHighlightedIndex(-1);
    }
  };

  const showDropdown = suggestions.length > 0 && inputValue.trim().length > 0;

  return (
    <div className="card">
      {stepIndicator}
      <div className="step-header">
        <h2>{hpTheme ? 'Lay Your Choices Before Me' : 'Step 1 — Your College List'}</h2>
        <p>
          {hpTheme
            ? "Name the schools between which you are torn. Give me at least 2 contenders if you want a proper sorting, and no more than 8 unless you fancy muddling my brim."
            : "Add the colleges you're deciding between. You need at least 2 schools for a meaningful comparison. Add up to 8 for the best results."}
        </p>
      </div>

      <div className="flex gap-2 mb-4" style={{ position: 'relative', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <input
            ref={inputRef}
            className="input"
            style={{ width: '100%' }}
            placeholder={hpTheme ? 'Speak a school name...' : 'Type a college name...'}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => inputValue.trim() && setSuggestions(searchSchools(inputValue))}
            autoComplete="off"
            autoFocus
          />

          {showDropdown && (
            <div ref={dropdownRef} className="autocomplete-dropdown">
              {suggestions.map((school, i) => (
                <div
                  key={school.name}
                  className={`autocomplete-item ${i === highlightedIndex ? 'highlighted' : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); addSchool(school.name); }}
                  onMouseEnter={() => setHighlightedIndex(i)}
                >
                  <span className="autocomplete-name">{school.name}</span>
                  {school.data && (
                    <span className="autocomplete-meta">{hpTheme ? 'known to the Hat' : 'data available'}</span>
                  )}
                </div>
              ))}
              {inputValue.trim() &&
               !suggestions.some(s => s.name.toLowerCase() === inputValue.trim().toLowerCase()) && (
                <div
                  className="autocomplete-item autocomplete-custom"
                  onMouseDown={(e) => { e.preventDefault(); addSchool(inputValue.trim()); }}
                  onMouseEnter={() => setHighlightedIndex(suggestions.length)}
                >
                  <span>{hpTheme ? 'Add' : 'Add'} "<strong>{inputValue.trim()}</strong>"</span>
                  <span className="autocomplete-meta">{hpTheme ? 'a name of your own' : 'custom entry'}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <button
          className="btn btn-primary"
          onClick={() => inputValue.trim() && addSchool(
            highlightedIndex >= 0 && suggestions[highlightedIndex]
              ? suggestions[highlightedIndex].name
              : inputValue.trim()
          )}
          disabled={!inputValue.trim()}
        >
          {hpTheme ? '+ Place Before The Hat' : '+ Add'}
        </button>
      </div>

      {schools.length === 0 && !hpTheme && (
        <div className="notice notice-info">
          Start typing a college name — known schools will appear as suggestions with pre-filled
          objective data (rankings, cost, career outcomes).
        </div>
      )}

      {schools.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
          {schools.map(school => (
            <span key={school} className="school-tag">
              {school}
              <button
                className="school-tag-remove"
                onClick={() => removeSchool(school)}
                title={`Remove ${school}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {schools.length === 1 && (
        <div className="notice notice-warning">
          {hpTheme ? 'One choice alone? Nonsense. Give me another school so I can make a true comparison.' : 'Add at least one more school to compare.'}
        </div>
      )}

      {schools.length > 5 && (
        <div className="notice notice-warning">
          {hpTheme ? 'A crowded mind makes for a noisy sorting. Consider trimming this list to your strongest 4 or 5 contenders.' : 'Comparing many schools can get complex. Consider narrowing to your top 4–5.'}
        </div>
      )}

      <div className="step-footer">
        <span className="text-muted">
          {schools.length} {hpTheme ? `contender${schools.length !== 1 ? 's' : ''} named` : `school${schools.length !== 1 ? 's' : ''} added`}
        </span>
        <button
          className="btn btn-primary"
          onClick={() => onNext(schools)}
          disabled={schools.length < 2}
        >
          {hpTheme ? 'Proceed To My Criteria →' : 'Continue to Factors →'}
        </button>
      </div>
    </div>
  );
}
